import { Link } from 'react-router-dom';
import { statusColour } from './orgChartApi';
import './orgChart.css';

function initials(name) {
  return String(name || '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('');
}

/**
 * 'YYYY-MM-DD' -> 'dd/mm/yyyy', by reordering the text. Never builds a Date:
 * new Date('2026-09-01') is UTC midnight, which is still 31 August anywhere west
 * of Greenwich. Anything not in that shape yields null, so the line is omitted
 * rather than showing garbage.
 */
function formatDay(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ''));
  return m ? `${m[3]}/${m[2]}/${m[1]}` : null;
}

/**
 * One org chart card, shared by the tree and the list so the two cannot drift.
 *
 * Seat styling keys off `kind`, never off a status value: plan status is free
 * text an admin types in Odoo, so any status-based check would break as soon as
 * someone invents a new label.
 */
export default function OrgNodeCard({ node, variant = 'tree', isAdmin = false }) {
  const isSeat = node.kind === 'seat';
  const cls = [
    'oc-card',
    `oc-card--${variant}`,
    isSeat ? 'oc-card--seat' : 'oc-card--person',
  ].join(' ');

  const avatar = isSeat ? (
    <span className="oc-avatar oc-avatar--seat" aria-hidden="true">+</span>
  ) : node.photo_url ? (
    <img className="oc-avatar" src={node.photo_url} alt="" />
  ) : (
    <span className="oc-avatar oc-avatar--fallback" aria-hidden="true">{initials(node.name)}</span>
  );

  const name = node.nexus_user_id ? (
    <Link className="oc-name oc-name--link" to={`/profile/${node.nexus_user_id}`}>{node.name}</Link>
  ) : (
    <span className={`oc-name${isSeat ? ' oc-name--seat' : ''}`}>{node.name}</span>
  );

  // The server sends this only beside an On Notice the viewer can see, so the
  // card renders whatever arrives rather than re-deciding who may see it.
  const lastDay = isSeat ? null : formatDay(node.departure_date);

  return (
    <article className={cls}>
      <div className="oc-head">
        {avatar}
        <div className="oc-identity">
          <div className="oc-name-row">
            {name}
          </div>
          {node.title && <p className="oc-title">{node.title}</p>}
        </div>
      </div>

      {/* Who is covering this vacancy (Odoo "Acting Employee"). Deliberately text
          on the seat card rather than a card of its own: the acting person
          already has a card on the job they actually hold, and giving them a
          second one would double-count them in the chart and in the counters.
          Seat cards only, and only when Odoo names someone. */}
      {isSeat && node.acting_name && (
        <p className="oc-acting">
          <span className="oc-acting-label">Acting</span>
          {/* Avatar in the LIST view only: the tree card is a fixed 285px column
              already carrying its own seat avatar, and a second one there reads
              as two people on one card. A list row is full width and has the
              horizontal space for it. */}
          {variant === 'list' && (
            node.acting_photo_url ? (
              <img className="oc-acting-avatar" src={node.acting_photo_url} alt="" />
            ) : (
              <span className="oc-acting-avatar oc-acting-avatar--fallback" aria-hidden="true">
                {initials(node.acting_name)}
              </span>
            )
          )}
          {/* Same type treatment as a person card's own name, so the reader sees
              a named human rather than a footnote on an empty seat. */}
          <span className="oc-acting-name">{node.acting_name}</span>
        </p>
      )}

      {!isSeat && node.manager_name && (
        <p className="oc-manager">
          Manager: {node.manager_name}
          {isAdmin && node.manager_is_mismatched && (
            <span
              className="oc-manager-warn"
              title="Odoo manager differs from the reporting line"
              aria-label="Odoo manager differs from the reporting line"
            >!</span>
          )}
        </p>
      )}

      <div className="oc-tags">
        {node.func && <span className="oc-tag oc-tag--func">{node.func}</span>}
        {node.org && <span className="oc-tag oc-tag--org">{node.org}</span>}
      </div>

      {node.status ? (
        <span
          className="oc-pill"
          style={isSeat
            ? { color: statusColour(node.status), borderColor: statusColour(node.status) }
            : { background: statusColour(node.status), borderColor: statusColour(node.status), color: '#fff' }}
        >
          {node.status}
        </span>
      ) : (
        // Keeps the row height stable and signals "withheld" rather than "broken".
        <span className={`oc-pill-slot${node.is_sensitive_hidden ? ' oc-pill-slot--hidden' : ''}`} />
      )}

      {lastDay && (
        <time className="oc-last-day" dateTime={node.departure_date}>
          {`Last day ${lastDay}`}
        </time>
      )}
    </article>
  );
}
